/**
 * Tiny animated "clipart" icons for the tutorial card: a stylised mouse or
 * keyboard key with the exact part the lesson cares about pulsing or sliding,
 * so "hold the RIGHT button and drag" reads at a glance instead of needing the
 * sentence to be parsed. Pure inline SVG + CSS keyframes (see tutorial.css) —
 * no image assets, so it reflows and reskins with the rest of the HUD.
 */

export type TutorialGesture =
  | { kind: 'click'; button: 'left' | 'right' }
  | { kind: 'drag'; button: 'left' | 'right' }
  | { kind: 'scroll' }
  | { kind: 'move' }
  | { kind: 'key'; label: string }
  | { kind: 'stroke' }
  | { kind: 'shift-drag' };

const SVG_NS = 'http://www.w3.org/2000/svg';

function svg(tag: string, attrs: Record<string, string>): SVGElement {
  const e = document.createElementNS(SVG_NS, tag);
  for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, v);
  return e;
}

/** The mouse body outline + the divider between its two buttons + a wheel notch. */
function mouseBody(): SVGElement[] {
  return [
    svg('rect', { x: '4', y: '2', width: '28', height: '40', rx: '13', class: 'tg-outline' }),
    svg('line', { x1: '18', y1: '3', x2: '18', y2: '15', class: 'tg-outline' }),
    svg('line', { x1: '5', y1: '16', x2: '31', y2: '16', class: 'tg-outline' }),
    svg('rect', { x: '16.5', y: '6', width: '3', height: '7', rx: '1.5', class: 'tg-outline tg-wheel' }),
  ];
}

/** One mouse button, filled and animated — the part the gesture actually needs. */
function mouseButton(side: 'left' | 'right', animClass: string): SVGElement {
  const x = side === 'left' ? '5' : '18.5';
  return svg('rect', { x, y: '3', width: '13', height: '12', rx: '4', class: `tg-btn ${animClass}` });
}

/** A short arrow sliding back and forth — "drag this way". */
function dragArrow(): SVGElement {
  const g = svg('g', { class: 'tg-arrow' });
  g.appendChild(svg('path', { d: 'M 3 44 h 22', class: 'tg-arrow-line' }));
  g.appendChild(svg('path', { d: 'M 25 40 l 5 4 l -5 4', class: 'tg-arrow-head' }));
  return g;
}

function buildMouseIcon(inner: (root: SVGSVGElement) => void): SVGSVGElement {
  const root = svg('svg', { viewBox: '0 0 40 48', class: 'tut-gesture-svg' }) as SVGSVGElement;
  for (const p of mouseBody()) root.appendChild(p);
  inner(root);
  return root;
}

function buildKeyIcon(label: string): SVGSVGElement {
  const root = svg('svg', { viewBox: '0 0 48 40', class: 'tut-gesture-svg tut-gesture-svg--key' }) as SVGSVGElement;
  root.appendChild(svg('rect', { x: '4', y: '4', width: '40', height: '30', rx: '7', class: 'tg-key' }));
  const text = svg('text', { x: '24', y: '25', class: 'tg-key-label' }) as SVGTextElement;
  text.textContent = label;
  root.appendChild(text);
  return root;
}

/** Build the animated icon for one gesture. Caller owns placement/sizing. */
export function buildGestureIcon(g: TutorialGesture): SVGSVGElement {
  switch (g.kind) {
    case 'click':
      return buildMouseIcon((root) => root.appendChild(mouseButton(g.button, 'tg-anim-click')));
    case 'drag':
      return buildMouseIcon((root) => {
        root.appendChild(mouseButton(g.button, 'tg-anim-hold'));
        root.appendChild(dragArrow());
      });
    case 'scroll':
      return buildMouseIcon((root) => {
        root.appendChild(svg('path', { d: 'M 14 2 l 4 -3 l 4 3', class: 'tg-scroll-arrow tg-scroll-up' }));
        root.appendChild(svg('path', { d: 'M 14 18 l 4 3 l 4 -3', class: 'tg-scroll-arrow tg-scroll-down' }));
      });
    case 'move':
      return buildMouseIcon((root) => root.appendChild(dragArrow()));
    case 'shift-drag':
      return buildMouseIcon((root) => {
        root.appendChild(mouseButton('left', 'tg-anim-hold'));
        root.appendChild(dragArrow());
      });
    case 'stroke': {
      const root = svg('svg', { viewBox: '0 0 64 32', class: 'tut-gesture-svg' }) as SVGSVGElement;
      root.appendChild(svg('circle', { cx: '52', cy: '16', r: '6', class: 'tg-outline' })); // the object ball
      root.appendChild(svg('line', { x1: '4', y1: '16', x2: '40', y2: '16', class: 'tg-cue tg-anim-stroke' }));
      return root;
    }
    case 'key':
      return buildKeyIcon(g.label);
  }
}

/** A wrapped gesture icon ready to drop into the panel. */
export function buildGestureBadge(g: TutorialGesture): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'tut-gesture';
  wrap.appendChild(buildGestureIcon(g));
  return wrap;
}
